import { useState, useMemo } from 'react';
import { WireframeBackground } from '@/components/WireframeBackground';
import { Link } from 'react-router-dom';
import { usePublicModels } from '@/hooks/useModels';
import { ModelGrid } from '@/components/model/ModelCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, SlidersHorizontal, RefreshCw } from 'lucide-react';
import type { ModelCardData } from '@/types/models';

type SortOption = 'newest' | 'popular' | 'most-forked';

export function Explore() {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useState(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  });

  // Fetch public models with React Query
  const { data, isLoading, isError, error, refetch } = usePublicModels({
    sortBy,
    search: debouncedSearch || undefined,
    limit: 24,
  });

  // Transform data to ModelCardData format
  const models: ModelCardData[] = useMemo(() => {
    if (!data?.models) return [];

    return data.models.map((model) => ({
      id: model.id,
      slug: model.slug,
      title: model.title,
      description: model.description,
      thumbnail_url: model.thumbnail_url,
      is_public: model.is_public,
      view_count: model.view_count,
      fork_count: model.fork_count || 0,
      created_at: model.created_at,
      updated_at: model.updated_at,
      author: {
        username: model.author.username,
        avatar_url: model.author.avatar_url,
      },
    }));
  }, [data]);

  return (
    <div className="relative flex h-full flex-col">
      <WireframeBackground />
      {/* Header */}
      <div className="relative z-10 border-b border-border px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Explore Models
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Discover and fork parametric OpenSCAD models created by the
              community
            </p>
          </div>
          <Link to="/new">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Create Model
            </Button>
          </Link>
        </div>

        {/* Search and filters */}
        <div className="mt-6 flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search models, users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={sortBy}
            onValueChange={(value) => setSortBy(value as SortOption)}
          >
            <SelectTrigger className="w-[160px]">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="most-forked">Most Forked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 overflow-auto p-8">
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-lg border border-border"
              >
                <div className="aspect-square bg-muted" />
                <div className="space-y-3 p-4">
                  <div className="h-5 w-3/4 rounded bg-muted" />
                  <div className="h-4 w-full rounded bg-muted" />
                  <div className="h-4 w-2/3 rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-destructive">
              Error loading models: {error?.message || 'Unknown error'}
            </p>
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="mt-4 gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          </div>
        ) : (
          <>
            <ModelGrid
              models={models}
              emptyMessage={
                searchQuery
                  ? `No models found for "${searchQuery}"`
                  : 'No models yet. Be the first to create one!'
              }
            />

            {/* Show total count */}
            {data && data.total > 0 && (
              <div className="mt-8 text-center text-sm text-muted-foreground">
                Showing {models.length} of {data.total} models
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
